/**
 * SnapshotButton — capture the first visible chart pane to PNG and download.
 *
 * Strategy: find the first element tagged `data-chart-snapshot="true"`
 * (TradingChart's wrapper), composite all its child <canvas> layers into a
 * single off-screen canvas via drawImage, then trigger a download. Also tries
 * to copy the blob to the system clipboard when the browser supports it.
 */
import React, { useState } from 'react';
import { IconButton, Tooltip, Snackbar } from '@mui/material';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';

interface Props {
  /** Filename hint (no extension). Falls back to "chart-<ts>". */
  filenameHint?: string;
}

const SnapshotButton: React.FC<Props> = ({ filenameHint }) => {
  const [toast, setToast] = useState<string | null>(null);

  const handleSnapshot = async () => {
    try {
      const host = document.querySelector<HTMLElement>('[data-chart-snapshot="true"]');
      if (!host) {
        setToast('No chart on screen to snapshot.');
        return;
      }
      const canvases = Array.from(host.querySelectorAll<HTMLCanvasElement>('canvas'));
      if (canvases.length === 0) {
        setToast('Chart canvas not ready yet.');
        return;
      }

      // Composite all canvases (lightweight-charts uses 2-3 stacked layers).
      const rect = host.getBoundingClientRect();
      const out = document.createElement('canvas');
      const dpr = window.devicePixelRatio || 1;
      out.width = Math.round(rect.width * dpr);
      out.height = Math.round(rect.height * dpr);
      const ctx = out.getContext('2d');
      if (!ctx) {
        setToast('Snapshot failed: could not create canvas.');
        return;
      }

      // Fill background to match app theme (otherwise we get transparency)
      const bg = getComputedStyle(host).backgroundColor || '#0d1117';
      ctx.fillStyle = bg && bg !== 'rgba(0, 0, 0, 0)' ? bg : '#0d1117';
      ctx.fillRect(0, 0, out.width, out.height);

      for (const c of canvases) {
        const cr = c.getBoundingClientRect();
        const dx = (cr.left - rect.left) * dpr;
        const dy = (cr.top - rect.top) * dpr;
        const dw = cr.width * dpr;
        const dh = cr.height * dpr;
        try { ctx.drawImage(c, dx, dy, dw, dh); }
        catch { /* Some canvases (e.g. WebGL) may be tainted; skip silently */ }
      }

      // Watermark
      ctx.fillStyle = 'rgba(255,255,255,0.55)';
      ctx.font = `${12 * dpr}px ui-monospace, monospace`;
      ctx.textBaseline = 'bottom';
      ctx.fillText(`earngush · ${new Date().toLocaleString('en-IN')}`, 8 * dpr, out.height - 6 * dpr);

      const blob: Blob | null = await new Promise(resolve => out.toBlob(b => resolve(b), 'image/png'));
      if (!blob) {
        setToast('Snapshot failed to encode.');
        return;
      }

      // Try the Clipboard API (browser support varies; gracefully fall back to download only)
      let copied = false;
      try {
        if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
          await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
          copied = true;
        }
      } catch { /* ignore — fall back to download only */ }

      // Always trigger a download (some users want the file)
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const name = `${filenameHint || 'chart'}-${ts}.png`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);

      setToast(copied ? `Snapshot saved + copied to clipboard` : `Snapshot saved as ${name}`);
    } catch (err: any) {
      setToast(`Snapshot failed: ${err?.message || 'unknown error'}`);
    }
  };

  return (
    <>
      <Tooltip title="Snapshot chart (PNG → download + clipboard)">
        <IconButton size="small" onClick={handleSnapshot} sx={{ p: 0.4 }}>
          <PhotoCameraOutlinedIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        message={toast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      />
    </>
  );
};

export default SnapshotButton;
